import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import {
	Animated,
	FlatList,
	FlatListProps,
	LayoutChangeEvent,
	NativeScrollEvent,
	NativeSyntheticEvent,
	ScrollView,
	ScrollViewProps,
	StyleProp,
	StyleSheet,
	View,
	ViewStyle,
} from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { useSvgId } from '../../../utils/hooks/useSvgId';
import {
	KeyboardScrollOptions,
	useKeyboardScrollArea,
} from '../../../utils/keyboard/KeyboardAvoidance';

/**
 * Figma "Scroll track" / "Scroll thumb" (Profile modal, 4897:22143 / 4897:22144): a 4-wide #5C2416
 * track down the right edge of the scrolling area, 8 clear of its top and bottom, and a thumb
 * fading from #FFF908 to #FFFF9E. Both are rounded to 2.
 */
export const SCROLL_INDICATOR = {
	width: 4,
	radius: 2,
	inset: 8,
	/** Room the design leaves between the content and the track. */
	gap: 8,
	trackColor: '#5C2416',
	thumbFrom: '#FFF908',
	thumbTo: '#FFFF9E',
	minThumbHeight: 32,
} as const;

export type ScrollIndicatorTheme = {
	trackColor: string;
	thumbFrom: string;
	thumbTo: string;
	/** The thumb's bloom, where the design gives it one. */
	thumbGlow?: string;
};

/**
 * Figma "Scrollbar" in the Shop (5360:61236): a darker #330803 track and an orange #FFE58C to
 * #E58C0D thumb with a soft orange glow - the one modal that doesn't use the default bar.
 */
export const SHOP_SCROLL_INDICATOR: ScrollIndicatorTheme = {
	trackColor: '#330803',
	thumbFrom: '#FFE58C',
	thumbTo: '#E58C0D',
	thumbGlow: 'rgba(255, 114, 0, 0.6)',
};

export type ScrollIndicatorInsets = { top?: number; right?: number; bottom?: number };

type IndicatorProps = {
	/** Distance of the bar from the scrolling area's edges; defaults to the design's 8. */
	indicatorInsets?: ScrollIndicatorInsets;
	/** Colours of the bar; defaults to the design's #5C2416 track and yellow thumb. */
	indicatorTheme?: ScrollIndicatorTheme;
	/** Hide the custom bar, e.g. for a list whose design draws its own. */
	hideIndicator?: boolean;
	/**
	 * Pad the content clear of the bar while it shows - the design keeps a gutter on the right
	 * for the track rather than drawing it over the content. On by default; content that already
	 * stops short of the bar (a narrow centred column) can turn it off.
	 */
	reserveGutter?: boolean;
};

const rightPaddingOf = (style: StyleProp<ViewStyle>) => {
	const flat = (StyleSheet.flatten(style) ?? {}) as ViewStyle;
	const value = flat.paddingRight ?? flat.paddingEnd ?? flat.paddingHorizontal ?? flat.padding ?? 0;
	return typeof value === 'number' ? value : 0;
};

/** The content's own right padding, widened to the gutter the bar needs while it shows. */
const withGutter = (
	contentContainerStyle: StyleProp<ViewStyle>,
	gutter: number,
): StyleProp<ViewStyle> =>
	gutter > rightPaddingOf(contentContainerStyle)
		? [contentContainerStyle, { paddingRight: gutter, paddingEnd: undefined }]
		: contentContainerStyle;

// The style a scroll view's frame takes: it moves onto the wrapper that also holds the bar, so the
// wrapper sits exactly where the scroll view used to and the scroll view simply fills it.
const FRAME_KEYS = new Set([
	'flex',
	'flexGrow',
	'flexShrink',
	'flexBasis',
	'width',
	'height',
	'minWidth',
	'maxWidth',
	'minHeight',
	'maxHeight',
	'aspectRatio',
	'alignSelf',
	'position',
	'top',
	'right',
	'bottom',
	'left',
	'start',
	'end',
	'zIndex',
	'elevation',
	'margin',
	'marginTop',
	'marginRight',
	'marginBottom',
	'marginLeft',
	'marginHorizontal',
	'marginVertical',
	'marginStart',
	'marginEnd',
	'transform',
]);

const splitFrameStyle = (style: StyleProp<ViewStyle>) => {
	const flat = (StyleSheet.flatten(style) ?? {}) as Record<string, unknown>;
	const frame: Record<string, unknown> = {};
	const inner: Record<string, unknown> = {};
	Object.keys(flat).forEach(key => {
		(FRAME_KEYS.has(key) ? frame : inner)[key] = flat[key];
	});
	return { frame: frame as ViewStyle, inner: inner as ViewStyle };
};

type ScrollHandlers = Pick<ScrollViewProps, 'onScroll' | 'onLayout' | 'onContentSizeChange'>;

/**
 * Tracks a vertical scroll view's viewport, content and offset and draws the design's bar over it.
 * The offset is fed to an Animated value rather than to state, so scrolling never re-renders the
 * list underneath - only the thumb moves.
 */
const useScrollIndicator = (
	handlers: ScrollHandlers,
	insets?: ScrollIndicatorInsets,
	theme: ScrollIndicatorTheme = SCROLL_INDICATOR,
) => {
	const [viewport, setViewport] = useState(0);
	const [content, setContent] = useState(0);
	const offset = useRef(new Animated.Value(0)).current;
	const handlersRef = useRef(handlers);
	handlersRef.current = handlers;

	const onScroll = useCallback(
		(event: NativeSyntheticEvent<NativeScrollEvent>) => {
			offset.setValue(event.nativeEvent.contentOffset.y);
			handlersRef.current.onScroll?.(event);
		},
		[offset],
	);
	const onLayout = useCallback((event: LayoutChangeEvent) => {
		setViewport(event.nativeEvent.layout.height);
		handlersRef.current.onLayout?.(event);
	}, []);
	const onContentSizeChange = useCallback((width: number, height: number) => {
		setContent(height);
		handlersRef.current.onContentSizeChange?.(width, height);
	}, []);

	const top = insets?.top ?? SCROLL_INDICATOR.inset;
	const bottom = insets?.bottom ?? SCROLL_INDICATOR.inset;
	const right = insets?.right ?? SCROLL_INDICATOR.inset;
	const trackHeight = viewport - top - bottom;
	const scrollable = viewport > 0 && content > viewport + 1 && trackHeight > 0;
	const thumbHeight = scrollable
		? Math.min(
				trackHeight,
				Math.max(SCROLL_INDICATOR.minThumbHeight, (trackHeight * viewport) / content),
			)
		: 0;
	const translateY = useMemo(
		() =>
			offset.interpolate({
				inputRange: [0, Math.max(1, content - viewport)],
				outputRange: [0, Math.max(0, trackHeight - thumbHeight)],
				extrapolate: 'clamp',
			}),
		[content, offset, thumbHeight, trackHeight, viewport],
	);

	const indicator = scrollable ? (
		<View
			pointerEvents='none'
			style={[
				styles.track,
				{ top, right, height: trackHeight, backgroundColor: theme.trackColor },
				// A glowing thumb blooms past the track, so the track must not clip it.
				theme.thumbGlow ? styles.trackUnclipped : null,
			]}
			testID='scroll-indicator'
		>
			<Animated.View
				style={[
					{ height: thumbHeight, transform: [{ translateY }] },
					theme.thumbGlow ? [styles.thumbGlow, { shadowColor: theme.thumbGlow }] : null,
				]}
			>
				<ScrollThumb height={thumbHeight} from={theme.thumbFrom} to={theme.thumbTo} />
			</Animated.View>
		</View>
	) : null;

	const gutter = scrollable ? right + SCROLL_INDICATOR.width + SCROLL_INDICATOR.gap : 0;

	return { handlers: { onScroll, onLayout, onContentSizeChange }, indicator, gutter };
};

const ScrollThumb = ({ height, from, to }: { height: number; from: string; to: string }) => {
	const gradientId = useSvgId('scrollThumb');
	return (
		<Svg width={SCROLL_INDICATOR.width} height={height}>
			<Defs>
				<LinearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
					<Stop offset='0' stopColor={from} />
					<Stop offset='1' stopColor={to} />
				</LinearGradient>
			</Defs>
			<Rect
				width={SCROLL_INDICATOR.width}
				height={height}
				rx={SCROLL_INDICATOR.radius}
				fill={`url(#${gradientId})`}
			/>
		</Svg>
	);
};

export type IndicatorScrollViewProps = ScrollViewProps &
	IndicatorProps & {
		/**
		 * Keep a focused text field inside in sight above the on-screen keyboard: while the keyboard
		 * covers the bottom of the scroll view it scrolls the field (and `submitRef`, when both
		 * fit) into view. Only for scroll views that hold text fields.
		 */
		avoidKeyboard?: boolean | KeyboardScrollOptions;
	};

/** Drop-in `ScrollView` that hides the platform indicator and draws the design's bar instead. */
export const IndicatorScrollView = forwardRef<ScrollView, IndicatorScrollViewProps>(
	(
		{
			style,
			contentContainerStyle,
			indicatorInsets,
			indicatorTheme,
			hideIndicator,
			reserveGutter = true,
			avoidKeyboard = false,
			onScroll,
			onLayout,
			onContentSizeChange,
			...props
		},
		ref,
	) => {
		const keyboard = useKeyboardScrollArea(
			!!avoidKeyboard,
			typeof avoidKeyboard === 'object' ? avoidKeyboard : undefined,
		);
		const setRef = useCallback(
			(node: ScrollView | null) => {
				keyboard.scrollRef.current = node;
				if (typeof ref === 'function') ref(node);
				else if (ref) ref.current = node;
			},
			[keyboard.scrollRef, ref],
		);
		const keyboardOnScroll = keyboard.onScroll;
		const keyboardOnLayout = keyboard.onLayout;
		const { handlers, indicator, gutter } = useScrollIndicator(
			{
				onScroll: avoidKeyboard
					? event => {
							keyboardOnScroll(event);
							onScroll?.(event);
						}
					: onScroll,
				onLayout: avoidKeyboard
					? event => {
							keyboardOnLayout(event);
							onLayout?.(event);
						}
					: onLayout,
				onContentSizeChange,
			},
			indicatorInsets,
			indicatorTheme,
		);
		if (props.horizontal || hideIndicator) {
			return (
				<ScrollView
					ref={ref}
					style={style}
					contentContainerStyle={contentContainerStyle}
					onScroll={onScroll}
					onLayout={onLayout}
					onContentSizeChange={onContentSizeChange}
					{...props}
				/>
			);
		}
		const { frame, inner } = splitFrameStyle(style);
		return (
			<View ref={keyboard.frame} style={[styles.frame, frame, keyboard.frameStyle]}>
				<ScrollView
					ref={setRef}
					scrollEventThrottle={16}
					{...props}
					contentContainerStyle={
						reserveGutter ? withGutter(contentContainerStyle, gutter) : contentContainerStyle
					}
					style={[styles.fill, inner]}
					showsVerticalScrollIndicator={false}
					{...handlers}
				/>
				{indicator}
			</View>
		);
	},
);
IndicatorScrollView.displayName = 'IndicatorScrollView';

type IndicatorFlatListProps<T> = FlatListProps<T> &
	IndicatorProps & {
		/** Ref to the underlying FlatList, for scrollToIndex and friends. */
		listRef?: React.Ref<FlatList<T>>;
	};

/** Drop-in `FlatList` that hides the platform indicator and draws the design's bar instead. */
// ItemT defaults to any, as it does on FlatList itself.
export const IndicatorFlatList = <T = any,>({
	style,
	contentContainerStyle,
	indicatorInsets,
	indicatorTheme,
	hideIndicator,
	reserveGutter = true,
	onScroll,
	onLayout,
	onContentSizeChange,
	listRef,
	...props
}: IndicatorFlatListProps<T>) => {
	const { handlers, indicator, gutter } = useScrollIndicator(
		{ onScroll, onLayout, onContentSizeChange },
		indicatorInsets,
		indicatorTheme,
	);
	if (props.horizontal || hideIndicator) {
		return (
			<FlatList
				ref={listRef}
				style={style}
				contentContainerStyle={contentContainerStyle}
				onScroll={onScroll}
				onLayout={onLayout}
				onContentSizeChange={onContentSizeChange}
				{...props}
			/>
		);
	}
	const { frame, inner } = splitFrameStyle(style);
	return (
		<View style={[styles.frame, frame]}>
			<FlatList
				ref={listRef}
				scrollEventThrottle={16}
				{...props}
				contentContainerStyle={
					reserveGutter ? withGutter(contentContainerStyle, gutter) : contentContainerStyle
				}
				style={[styles.fill, inner]}
				showsVerticalScrollIndicator={false}
				{...handlers}
			/>
			{indicator}
		</View>
	);
};

const styles = StyleSheet.create({
	frame: { minHeight: 0 },
	// The scroll view fills the frame when the frame is sized, and sizes it from its content when
	// it is not - a frame capped by maxHeight still lets the list shrink to the cap and scroll.
	fill: { flexGrow: 1, flexShrink: 1, minHeight: 0 },
	track: {
		position: 'absolute',
		width: SCROLL_INDICATOR.width,
		borderRadius: SCROLL_INDICATOR.radius,
		overflow: 'hidden',
	},
	trackUnclipped: { overflow: 'visible' },
	// Figma: 0 0 6px at 60%.
	thumbGlow: {
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 1,
		shadowRadius: 6,
		borderRadius: SCROLL_INDICATOR.radius,
	},
});
